import {
  buildSharedMeetingUrl,
  copyShareLink,
  type NormalizedShareStatus,
  type ShareLinkAdapter,
  useShareLink,
  type UseShareLinkReturn,
} from '@littlebird/ui-utils';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { createShareNotify } from '@/components/blocks/share/share-notify';
import { ShareAccessControls } from '@/components/blocks/share/ShareAccessControls';
import { SharePopover } from '@/components/blocks/share/SharePopover';
import { ShareSocialButtons } from '@/components/blocks/share/ShareSocialButtons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { getEnvVar } from '@/env';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import { teamStore } from '../team/team.store';

import { meetingApis } from './meetings.query';

const BASE_URL = getEnvVar('BASE_URL_FOR_PUBLIC_LINKS');

const notify = createShareNotify(
  'Failed to share meeting',
  'Failed to share meeting, please try again'
);

const SHARE_MEETING_TITLE = 'Share meeting';
const SHARE_MEETING_SUBTITLE = 'Choose who can view the summary';

function useShareMeeting(meetingId: string, open: boolean) {
  const canShareBeyondPrivate = !userStore.hasSignedBaa;
  const hasTeam = !!teamStore.dashboard;
  const baseUrl = BASE_URL ?? window.location.origin;
  const canInvitePeople = useFeature()(FEATURES.SHARE_WITH_PEOPLE);

  const adapter = useMemo<ShareLinkAdapter>(
    () => ({
      async getStatus(id): Promise<NormalizedShareStatus> {
        const r = await meetingApis.getMeetingShareStatus(id);
        return {
          isShared: r.is_shared,
          publicToken: r.public_token,
          visibility: r.visibility,
          sharedTeamId: r.shared_team_id,
          invitedEmails: r.invited_emails,
        };
      },
      async setVisibility(id, visibility) {
        const r = await meetingApis.setMeetingAsPublic(id, visibility);
        return {
          publicToken: r.public_token,
          sharedTeamId: r.shared_team_id,
        };
      },
      async revoke(id) {
        await meetingApis.revokeMeetingShare(id);
      },
      invites: canInvitePeople
        ? {
            add: meetingApis.addMeetingShareInvite,
            remove: meetingApis.removeMeetingShareInvite,
          }
        : undefined,
      buildUrl(ids, tokens) {
        return buildSharedMeetingUrl(ids[0]!, tokens[0]!, baseUrl);
      },
      onLinkCreated(url) {
        return copyShareLink(url, notify);
      },
      onShared(ids) {
        trackEvent('MEETING_SHARED', { meeting_id: ids[0] });
      },
      onUnshared(ids) {
        trackEvent('MEETING_UNSHARED', { meeting_id: ids[0] });
      },
      ...notify,
    }),
    [baseUrl, canInvitePeople]
  );

  return useShareLink({
    ids: [meetingId],
    adapter,
    open,
    supportsTeam: true,
    canShareBeyondPrivate,
    hasTeam,
  });
}

const Content = ({ link }: { link: UseShareLinkReturn }) => (
  <ShareAccessControls
    link={link}
    idPrefix="share-meeting"
    linkFooter={
      link.shareStatus?.visibility === 'public' && link.url ? (
        <ShareSocialButtons url={link.url} />
      ) : null
    }
  />
);

type ShareMeetingPopoverProps = {
  meetingId: string;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Position the popover against `children` without making it the trigger. For callers that open
   * the popover from elsewhere (e.g. a dropdown item) but want it anchored to a visible element.
   */
  anchorOnly?: boolean;
};

export const ShareMeetingPopover = ({
  meetingId,
  children,
  open: controlledOpen,
  onOpenChange,
  anchorOnly = false,
}: ShareMeetingPopoverProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );
  const link = useShareMeeting(meetingId, open);

  return (
    <SharePopover
      anchor={children}
      anchorOnly={anchorOnly}
      open={open}
      onOpenChange={setOpen}
      title={SHARE_MEETING_TITLE}
      description={SHARE_MEETING_SUBTITLE}
    >
      <Content link={link} />
    </SharePopover>
  );
};

type ShareMeetingModalProps = {
  meetingId: string;
};

export const ShareMeetingModal: ModalFC<ShareMeetingModalProps> = ({
  meetingId,
  open,
  onOpenChange,
}) => {
  const link = useShareMeeting(meetingId, open);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="overflow-hidden max-w-sm gap-3 px-4 pt-3.5 pb-0">
        <Dialog.Header className="space-y-0.5 pr-8">
          <Dialog.Title className="text-sm font-medium">
            {SHARE_MEETING_TITLE}
          </Dialog.Title>
          <Dialog.Description className="text-xs">
            {SHARE_MEETING_SUBTITLE}
          </Dialog.Description>
        </Dialog.Header>
        <Content link={link} />
      </Dialog.Content>
    </Dialog.Root>
  );
};
